import React from "react";
import { FormControl, FormGroup, FormHelperText, FormLabel } from "@mui/material";
import { DataFormControl } from "../DataFormControl";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorTextList } from "../../../CommonUI/PropertyEditing/PropertyEditorTextList";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { StyledFormControlLabel } from "../../../CommonUI/BuildlingBlocks/StyledFormControlLabel";
import { StyledCheckBox } from "../../../CommonUI/BuildlingBlocks/StyledCheckbox";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";

class XCheckboxListEditUI extends React.Component<{ editMe: CheckboxListFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorTextList
                    editObject={this.props.editMe}
                    propertyName={"list"}
                    label="List"
                />
                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    propertyName={"requiredOneSelectionAtLeast"}
                    label="Required One Selection At Least"
                />
            </UISpacedBoxContent>
        );
    }
}

export class CheckboxListFormControl extends DataFormControl {
    readonly __type: string = "Checkbox List";
    list: string[] = [];
    requiredOneSelectionAtLeast: boolean = false;

    getRuntimeValidationIssues(): ValidationIssue[] {
        const issues = super.getRuntimeValidationIssues();
        const runtimeIssues = this.validate();
        if (runtimeIssues) {
            for (let val of runtimeIssues) {
                issues.push(new ValidationIssue(val, this.valueName, this.id));
            }
        }

        return issues;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        const issues = super.getRuntimeValidationIssues();

        // if duplicates exists
        if (Array.from(new Set(this.list)).length !== this.list.length) {
            issues.push(new ValidationIssue("Items should be unique", undefined, this.id));
        }

        return issues;
    }

    render() {
        let items = this.list;
        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string | undefined | null = issueData?.text;
        return (
            <FormControl
                component="fieldset"
                data-testid={this.valueName}
                error={!!issueText}
            >
                <FormLabel component="legend">{this.label}</FormLabel>
                <FormGroup>
                    {items.map((item: string, index: number) => (
                        <StyledFormControlLabel
                            control={
                                <StyledCheckBox
                                    name={item}
                                    key={index}
                                    checked={this.getCheckedByName(item)}
                                    onChange={this.handleChange}
                                />
                            }
                            key={index}
                            label={item}
                        />
                    ))}
                </FormGroup>
                <FormHelperText>{issueText}</FormHelperText>
            </FormControl>
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XCheckboxListEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.list = true;
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "list", "requiredOneSelectionAtLeast");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "list", "requiredOneSelectionAtLeast");
    }

    private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, { ...this.getValue(), [event.target.name]: event.target.checked });
        }
    };

    private getValue = (): { [key: string]: boolean } => {
        let value = {};
        if (this.valueName) {
            value = this.getFormDataValue(this.valueName) || {};
        }
        return value;
    };

    private getCheckedByName = (item: string) => {
        return this.getValue()[item];
    };

    private validate = () => {
        let errorMessages: string[] = [];
        let value = this.valueName ? this.getFormDataValue(this.valueName) : undefined;
        if (value && this.requiredOneSelectionAtLeast) {
            if (!Object.keys(value).find((k: string) => !!value[k])) {
                errorMessages.push("Choose one or more options");
            }
        }
        return errorMessages;
    };
}
